import React from 'react';
import styled from 'styled-components';
import { Package, Calendar, User, ArrowRight } from 'lucide-react';
import { format } from 'date-fns';
import { Project } from '../types';

const Card = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing[4]};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const ProjectInfo = styled.div`
  flex: 1;
`;

const ProjectCode = styled.h3`
  font-size: ${({ theme }) => theme.typography.lg};
  font-weight: ${({ theme }) => theme.typography.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing[1]} 0;
`;

const SONumber = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
`;

const CustomerName = styled.p`
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: ${({ theme }) => theme.spacing[2]} 0;
`;

const ProjectMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  font-size: ${({ theme }) => theme.typography.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.sm};
  cursor: pointer;
  transition: ${({ theme }) => theme.transition.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }
`;

const StatusBadge = styled.div<{ $status: string }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: ${({ theme }) => theme.typography.xs};
  font-weight: ${({ theme }) => theme.typography.medium};
  text-transform: capitalize;
  background: ${({ theme, $status }) => {
    switch ($status) {
      case 'completed':
        return theme.colors.successLight;
      case 'in-progress':
        return theme.colors.warningLight;
      case 'cancelled':
        return theme.colors.errorLight;
      default:
        return theme.colors.gray100;
    }
  }};
  color: ${({ theme, $status }) => {
    switch ($status) {
      case 'completed':
        return theme.colors.success;
      case 'in-progress':
        return theme.colors.warning;
      case 'cancelled':
        return theme.colors.error;
      default:
        return theme.colors.text;
    }
  }};
`;

export interface ProjectCardProps {
  project: Project;
  onClick: () => void;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onClick }) => {
  return (
    <Card onClick={onClick}>
      <CardHeader>
        <ProjectInfo>
          <ProjectCode>{project.code || project.id}</ProjectCode>
          {project.so_number && (
            <SONumber>SO: {project.so_number}</SONumber>
          )}
          <CustomerName>{project.customer_name || 'No Customer'}</CustomerName>
        </ProjectInfo>
        <StatusBadge $status={project.status}>
          {project.status.replace('-', ' ')}
        </StatusBadge>
      </CardHeader>

      <ProjectMeta>
        <MetaItem>
          <span>Order ID:</span>
          <span>{project.order_id || 'N/A'}</span>
        </MetaItem>
        <MetaItem>
          <span>Order Date:</span>
          <span>{project.order_date ? new Date(project.order_date).toLocaleDateString() : 'N/A'}</span>
        </MetaItem>
        {project.statistics?.cartons && (
          <MetaItem>
            <span>Total Cartons:</span>
            <span>{project.statistics.cartons.total_cartons}</span>
          </MetaItem>
        )}
      </ProjectMeta>

      <ViewButton>
        View Details
      </ViewButton>
    </Card>
  );
}; 